import { useState, useEffect } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  ArrowLeft,
  User,
  Building2,
  MapPin,
  Calendar,
  DollarSign,
  Shield,
  Package,
  History,
  Clock,
  UserCheck,
  RotateCcw,
  X,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Download,
  FileText,
  Image,
  Laptop,
  Monitor,
  Keyboard,
  Mouse,
  Headphones,
  Video,
  Tablet,
  Smartphone,
  Server,
  Wifi,
  Battery,
  Sofa,
  Layout,
  Users,
  Archive,
  Printer,
  Search,
  Projector,
  Copy,
  Wind,
  Car,
  Zap,
  HardDrive,
  Wrench,
  ShieldCheck,
  AlertTriangle,
  File,
  FolderOpen,
  Upload,
  Trash,
  ExternalLink,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@jira-clone/ui'
import {
  useAssetDetail,
  useAssetHistory,
  useAssetMaintenance,
  useAssetDocuments,
  useAssetMutations,
  useAssetMaintenanceMutations,
  useAssetDocumentMutations,
  useUsersForAssignment,
  type AssetOption,
  type AssetCategory,
  type AssetStatus,
  type AssetDocumentType,
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  STATUS_LABELS,
  STATUS_COLORS,
  DOCUMENT_TYPE_LABELS,
} from '../../shared/hooks/useCrmVocabulary'
import { getApiErrorMessage } from '../../shared/utils/apiError'
import { formatDistanceToNow } from 'date-fns'

const CATEGORY_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Laptop,
  Monitor,
  Keyboard,
  Mouse,
  Headphones,
  Video,
  Tablet,
  Smartphone,
  Server,
  Wifi,
  Battery,
  Sofa,
  Layout,
  Users,
  Archive,
  Printer,
  Search,
  Projector,
  Copy,
  Wind,
  Car,
  Zap,
  Package,
  Image,
  FileText,
  Shield,
  HardDrive,
}

function CategoryIcon({ iconName, className = 'h-5 w-5' }: { iconName: string; className?: string }) {
  const IconComponent = CATEGORY_ICON_MAP[iconName] || Package
  return <IconComponent className={className} />
}

function StatusBadge({ status }: { status: AssetStatus }) {
  return (
    <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </Badge>
  )
}

function ActionIcon({ action }: { action: string }) {
  switch (action) {
    case 'CREATED':
      return <Package className="h-4 w-4 text-emerald-600" />
    case 'ASSIGNED':
      return <UserCheck className="h-4 w-4 text-blue-600" />
    case 'RETURNED':
      return <RotateCcw className="h-4 w-4 text-amber-600" />
    case 'TRANSFERRED':
      return <RotateCcw className="h-4 w-4 text-indigo-600" />
    case 'LOCATION_CHANGED':
      return <MapPin className="h-4 w-4 text-sky-600" />
    case 'DEPARTMENT_CHANGED':
      return <Building2 className="h-4 w-4 text-violet-600" />
    case 'STATUS_CHANGED':
      return <Shield className="h-4 w-4 text-orange-600" />
    case 'MAINTENANCE_SENT':
      return <Shield className="h-4 w-4 text-red-600" />
    case 'MAINTENANCE_RETURNED':
      return <RotateCcw className="h-4 w-4 text-emerald-600" />
    case 'RETIRED':
      return <Archive className="h-4 w-4 text-slate-600" />
    case 'DISPOSED':
      return <Trash2 className="h-4 w-4 text-destructive" />
    case 'UPDATED':
      return <History className="h-4 w-4 text-slate-500" />
    case 'DOCUMENT_ADDED':
      return <FileText className="h-4 w-4 text-green-600" />
    case 'DOCUMENT_REMOVED':
      return <X className="h-4 w-4 text-red-600" />
    case 'WARRANTY_UPDATED':
      return <Shield className="h-4 w-4 text-amber-600" />
    default:
      return <History className="h-4 w-4 text-slate-500" />
  }
}

function ActionLabel({ action }: { action: string }) {
  const labels: Record<string, string> = {
    CREATED: 'Created',
    ASSIGNED: 'Assigned',
    RETURNED: 'Returned',
    TRANSFERRED: 'Transferred',
    LOCATION_CHANGED: 'Location Changed',
    DEPARTMENT_CHANGED: 'Department Changed',
    STATUS_CHANGED: 'Status Changed',
    MAINTENANCE_SENT: 'Maintenance Started',
    MAINTENANCE_RETURNED: 'Maintenance Completed',
    RETIRED: 'Retired',
    DISPOSED: 'Disposed',
    UPDATED: 'Updated',
    DOCUMENT_ADDED: 'Document Added',
    DOCUMENT_REMOVED: 'Document Removed',
    WARRANTY_UPDATED: 'Warranty Updated',
  }
  return labels[action] || action
}

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const assetQuery = useAssetDetail(id)
  const historyQuery = useAssetHistory(id || '')
  const maintenanceQuery = useAssetMaintenance(id || '')
  const documentsQuery = useAssetDocuments(id || '')
  const mutations = useAssetMutations()
  const maintenanceMutations = useAssetMaintenanceMutations()
  const documentMutations = useAssetDocumentMutations()
  const usersQuery = useUsersForAssignment()
  const users = usersQuery.data || []

  const asset = assetQuery.data
  const history = historyQuery.data || []
  const maintenanceRecords = maintenanceQuery.data || []
  const documents = documentsQuery.data || []
  const isLoading = assetQuery.isLoading || historyQuery.isLoading || maintenanceQuery.isLoading || documentsQuery.isLoading

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  // Assignment dialogs
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [returnDialogOpen, setReturnDialogOpen] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  // Maintenance dialogs
  const [sendMaintenanceDialogOpen, setSendMaintenanceDialogOpen] = useState(false)
  const [sendMaintenanceForm, setSendMaintenanceForm] = useState({
    maintenanceType: 'PREVENTIVE',
    description: '',
    vendor: '',
    cost: '',
    maintenanceDate: new Date().toISOString().split('T')[0],
    nextMaintenanceDate: '',
    notes: '',
  })
  // Document dialogs
  const [uploadDocumentDialogOpen, setUploadDocumentDialogOpen] = useState(false)
  const [uploadDocumentForm, setUploadDocumentForm] = useState({
    documentType: 'PURCHASE_INVOICE',
    fileName: '',
    fileUrl: '',
    fileType: '',
    fileSize: '',
    description: '',
  })

  const [assignForm, setAssignForm] = useState({
    assignedTo: '',
    department: '',
    location: '',
    assignmentDate: new Date().toISOString().split('T')[0],
    notes: '',
  })
  const [returnForm, setReturnForm] = useState({ notes: '' })
  const [transferForm, setTransferForm] = useState({
    assignedTo: '',
    department: '',
    location: '',
    transferDate: new Date().toISOString().split('T')[0],
    notes: '',
  })

  if (!id) return null

  const openAssign = () => {
    if (!asset) return
    setAssignForm({
      assignedTo: '',
      department: asset.physicalData?.department || '',
      location: asset.physicalData?.location || '',
      assignmentDate: new Date().toISOString().split('T')[0],
      notes: '',
    })
    setAssignDialogOpen(true)
  }

  const openReturn = () => {
    setReturnForm({ notes: '' })
    setReturnDialogOpen(true)
  }

  const openTransfer = () => {
    if (!asset) return
    setTransferForm({
      assignedTo: '',
      department: asset.physicalData?.department || '',
      location: asset.physicalData?.location || '',
      transferDate: new Date().toISOString().split('T')[0],
      notes: '',
    })
    setTransferDialogOpen(true)
  }

  const handleAssign = async () => {
    if (!asset || !assignForm.assignedTo) {
      toast.error('Please select an employee to assign the asset to.')
      return
    }
    const selectedUser = users.find((u: { id: string; department?: string }) => u.id === assignForm.assignedTo)
    try {
      await mutations.assign.mutateAsync({
        id: asset.id,
        payload: {
          assignedTo: assignForm.assignedTo,
          department: assignForm.department || selectedUser?.department || '',
          location: assignForm.location,
          assignmentDate: assignForm.assignmentDate,
          notes: assignForm.notes,
        },
      })
      toast.success('Asset assigned successfully.')
      setAssignDialogOpen(false)
      assetQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'assign this asset'))
    }
  }

  const handleReturn = async () => {
    if (!asset) return
    try {
      await mutations.returnAsset.mutateAsync({
        id: asset.id,
        payload: { notes: returnForm.notes },
      })
      toast.success('Asset returned successfully.')
      setReturnDialogOpen(false)
      assetQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'return this asset'))
    }
  }

  const handleTransfer = async () => {
    if (!asset || !transferForm.assignedTo) {
      toast.error('Please select an employee to transfer the asset to.')
      return
    }
    const selectedUser = users.find((u: { id: string; department?: string }) => u.id === transferForm.assignedTo)
    try {
      await mutations.transfer.mutateAsync({
        id: asset.id,
        payload: {
          assignedTo: transferForm.assignedTo,
          department: transferForm.department || selectedUser?.department || '',
          location: transferForm.location,
          transferDate: transferForm.transferDate,
          notes: transferForm.notes,
        },
      })
      toast.success('Asset transferred successfully.')
      setTransferDialogOpen(false)
      assetQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'transfer this asset'))
    }
  }

  const openSendMaintenance = () => {
    setSendMaintenanceForm({
      maintenanceType: 'PREVENTIVE',
      description: '',
      vendor: '',
      cost: '',
      maintenanceDate: new Date().toISOString().split('T')[0],
      nextMaintenanceDate: '',
      notes: '',
    })
    setSendMaintenanceDialogOpen(true)
  }

  const handleSendMaintenance = async () => {
    if (!asset || !sendMaintenanceForm.description) {
      toast.error('Please provide a description for the maintenance.')
      return
    }
    try {
      await maintenanceMutations.create.mutateAsync({
        assetId: asset.id,
        payload: {
          maintenanceType: sendMaintenanceForm.maintenanceType,
          description: sendMaintenanceForm.description,
          vendor: sendMaintenanceForm.vendor,
          cost: sendMaintenanceForm.cost ? Number(sendMaintenanceForm.cost) : undefined,
          maintenanceDate: sendMaintenanceForm.maintenanceDate,
          nextMaintenanceDate: sendMaintenanceForm.nextMaintenanceDate || undefined,
          notes: sendMaintenanceForm.notes || undefined,
        },
      })
      toast.success('Asset sent for maintenance.')
      setSendMaintenanceDialogOpen(false)
      assetQuery.refetch()
      maintenanceQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'send asset for maintenance'))
    }
  }

  const handleCompleteMaintenance = async (maintenanceId: string) => {
    if (!asset) return
    const notes = prompt('Optional completion notes:')
    try {
      await maintenanceMutations.complete.mutateAsync({
        assetId: asset.id,
        maintenanceId,
        payload: { notes: notes || undefined },
      })
      toast.success('Maintenance completed.')
      assetQuery.refetch()
      maintenanceQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'complete maintenance'))
    }
  }

  const handleCancelMaintenance = async (maintenanceId: string) => {
    if (!asset) return
    if (!confirm('Cancel this maintenance record? The asset status will be reverted.')) return
    try {
      await maintenanceMutations.cancel.mutateAsync({
        assetId: asset.id,
        maintenanceId,
      })
      toast.success('Maintenance record cancelled.')
      assetQuery.refetch()
      maintenanceQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'cancel maintenance'))
    }
  }

  const handleDeleteDocument = async (documentId: string) => {
    if (!asset) return
    if (!confirm('Remove this document permanently?')) return
    try {
      await documentMutations.remove.mutateAsync({ assetId: asset.id, documentId })
      toast.success('Document removed.')
      documentsQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'remove document'))
    }
  }

  const openUploadDocument = () => {
    setUploadDocumentForm({
      documentType: 'PURCHASE_INVOICE',
      fileName: '',
      fileUrl: '',
      fileType: '',
      fileSize: '',
      description: '',
    })
    setUploadDocumentDialogOpen(true)
  }

  const handleUploadDocument = async () => {
    if (!asset || !uploadDocumentForm.fileName || !uploadDocumentForm.fileUrl || !uploadDocumentForm.fileType || !uploadDocumentForm.fileSize) {
      toast.error('Please fill in all required fields.')
      return
    }
    try {
      await documentMutations.add.mutateAsync({
        assetId: asset.id,
        payload: {
          documentType: uploadDocumentForm.documentType,
          fileName: uploadDocumentForm.fileName,
          fileUrl: uploadDocumentForm.fileUrl,
          fileType: uploadDocumentForm.fileType,
          fileSize: Number(uploadDocumentForm.fileSize),
          description: uploadDocumentForm.description,
        },
      })
      toast.success('Document uploaded successfully.')
      setUploadDocumentDialogOpen(false)
      documentsQuery.refetch()
      historyQuery.refetch()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'upload document'))
    }
  }

  const getWarrantyStatus = () => {
    if (!asset || asset.assetType !== 'PHYSICAL' || !asset.physicalData) return null
    const pd = asset.physicalData
    if (!pd.warrantyEndDate) return { label: 'No Warranty', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', icon: Shield }
    const endDate = new Date(pd.warrantyEndDate)
    const now = new Date()
    const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return { label: 'Expired', color: 'bg-destructive/10 text-destructive border-destructive', icon: AlertTriangle }
    if (diffDays <= 30) return { label: 'Expiring Soon', color: 'bg-warning/10 text-warning border-warning', icon: AlertTriangle }
    return { label: 'Active', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: ShieldCheck }
  }

  const handleDelete = async () => {
    if (!asset) return
    if (!confirm(`Delete "${asset.name}"? This can't be undone.`)) return
    setDeletingId(asset.id)
    try {
      await mutations.remove.mutateAsync(asset.id)
      toast.success('Asset deleted.')
      window.history.back()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this asset'))
    } finally {
      setDeletingId(null)
    }
  }

  const handlePreview = () => {
    if (!asset?.fileUrl) return
    setPreviewOpen(true)
  }

  if (assetQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <p className="text-destructive">Failed to load asset</p>
        <Button variant="outline" onClick={() => window.history.back()} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    )
  }

  if (isLoading || !asset) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-6 bg-muted rounded w-1/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const isPhysical = asset.assetType === 'PHYSICAL'
  const pd = asset.physicalData
  const dd = asset.digitalData
  const catIcon = CATEGORY_ICONS[asset.category as AssetCategory] || 'Package'

  const [searchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') || 'details'
  const validTabs = ['details', 'purchase', 'assignment', 'warranty', 'maintenance', 'documents', 'history']
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'details'
  const [activeTab, setActiveTab] = useState(initialTab)

  // Sync tab when URL changes (e.g. back/forward or direct navigation)
  useEffect(() => {
    const t = searchParams.get('tab')
    if (t && validTabs.includes(t)) setActiveTab(t)
  }, [searchParams])

  // Reset to correct tab if the URL has a tab param and we're on the wrong one
  useEffect(() => {
    if (tabFromUrl !== activeTab && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl)
    }
  }, [asset?.id])

  const onTabChange = (value: string) => {
    setActiveTab(value)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', value)
    window.history.replaceState({}, '', url.toString())
  }

  return (
    <div className="space-y-5 max-w-7xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Assets
        </Button>
        <div className="flex items-center gap-3">
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${STATUS_COLORS[asset.status]}`}>
            <CategoryIcon iconName={catIcon} className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{asset.name}</h1>
            <p className="text-sm text-muted-foreground">
              {CATEGORY_LABELS[asset.category]} • {isPhysical ? 'Physical Asset' : 'Digital Asset'}
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <StatusBadge status={asset.status} />
          {isPhysical && pd?.assetCode && (
            <Badge variant="secondary" className="text-xs h-5 px-2 font-mono">
              {pd.assetCode}
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7">
          <TabsTrigger value="details">Asset Info</TabsTrigger>
          <TabsTrigger value="purchase">Purchase</TabsTrigger>
          {isPhysical && <TabsTrigger value="assignment">Assignment</TabsTrigger>}
          {isPhysical && <TabsTrigger value="warranty">Warranty</TabsTrigger>}
          {isPhysical && <TabsTrigger value="maintenance">Maintenance</TabsTrigger>}
          {isPhysical && <TabsTrigger value="documents">Documents</TabsTrigger>}
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="pt-4">
          <div className="grid gap-4 md:grid-cols-3">
            {/* Main Info Card */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Asset Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="font-medium">{asset.name}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <div className="flex items-center gap-2">
                      <CategoryIcon iconName={catIcon} className="h-4 w-4" />
                      <span>{CATEGORY_LABELS[asset.category]}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <StatusBadge status={asset.status} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Asset Type</Label>
                    <Badge variant="outline">{isPhysical ? 'Physical' : 'Digital'}</Badge>
                  </div>
                  {pd?.assetCode && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Asset Code</Label>
                      <p className="font-mono font-medium">{pd.assetCode}</p>
                    </div>
                  )}
                  {pd?.serialNumber && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Serial Number</Label>
                      <p className="font-mono">{pd.serialNumber}</p>
                    </div>
                  )}
                  {pd?.brand && pd?.model && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Brand / Model</Label>
                      <p className="font-medium">{pd.brand} {pd.model}</p>
                    </div>
                  )}
                  {pd?.purchaseDate && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Purchase Date</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(pd.purchaseDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {pd?.purchasePrice && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Purchase Price</Label>
                      <p className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        ₹{pd.purchasePrice.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {pd?.currentValue && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Current Value</Label>
                      <p className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        ₹{pd.currentValue.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {pd?.vendor && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Vendor</Label>
                      <p>{pd.vendor}</p>
                    </div>
                  )}
                  {pd?.invoiceNumber && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Invoice Number</Label>
                      <p className="font-mono">{pd.invoiceNumber}</p>
                    </div>
                  )}
                  {pd?.purchaseOrderNumber && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PO Number</Label>
                      <p className="font-mono">{pd.purchaseOrderNumber}</p>
                    </div>
                  )}
                  {pd?.warrantyStartDate && pd?.warrantyEndDate && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Warranty</Label>
                      <div className="flex items-center gap-1">
                        <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>
                          {new Date(pd.warrantyStartDate).toLocaleDateString()} —{' '}
                          {new Date(pd.warrantyEndDate).toLocaleDateString()}
                          {' '}
                          <Badge
                            variant="outline"
                            className={
                              new Date(pd.warrantyEndDate) < new Date()
                                ? 'bg-destructive/10 text-destructive border-destructive'
                                : new Date(pd.warrantyEndDate).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000
                                ? 'bg-warning/10 text-warning border-warning'
                                : ''
                            }
                          >
                            {asset.warrantyStatus || 'Unknown'}
                          </Badge>
                        </span>
                      </div>
                    </div>
                  )}
                  {pd?.warrantyProvider && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Warranty Provider</Label>
                      <p>{pd.warrantyProvider}</p>
                    </div>
                  )}
                  {pd?.assignedToName && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Assigned To</Label>
                      <p className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.assignedToName}
                      </p>
                    </div>
                  )}
                  {pd?.department && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Department</Label>
                      <p className="flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.department}
                      </p>
                    </div>
                  )}
                  {pd?.location && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Location</Label>
                      <p className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.location}
                      </p>
                    </div>
                  )}
                  {pd?.assignmentDate && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Assignment Date</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(pd.assignmentDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {asset.description && (
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Description</Label>
                      <p className="text-sm">{asset.description}</p>
                    </div>
                  )}
                  {pd?.notes && (
                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Notes</Label>
                      <p className="text-sm">{pd.notes}</p>
                    </div>
                  )}
                </div>

                {/* Digital Asset Info */}
                {!isPhysical && dd && (
                  <div className="grid gap-4 sm:grid-cols-2 border-t border-border pt-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">File Name</Label>
                      <p className="font-mono text-sm">{dd.fileName}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">File Type</Label>
                      <p>{dd.fileType}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">File Size</Label>
                      <p>
                        {dd.fileSize < 1024
                          ? `${dd.fileSize} B`
                          : dd.fileSize < 1024 * 1024
                          ? `${(dd.fileSize / 1024).toFixed(1)} KB`
                          : `${(dd.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                      </p>
                    </div>
                    {dd.version && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Version</Label>
                        <p>v{dd.version}</p>
                      </div>
                    )}
                    {dd.tags && dd.tags.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Tags</Label>
                        <div className="flex flex-wrap gap-1">
                          {dd.tags.map((tag: string) => (
                            <Badge key={tag} variant="secondary" className="text-[10px] h-4 px-1.5">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {dd.expiryDate && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Expiry Date</Label>
                        <p className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {new Date(dd.expiryDate).toLocaleDateString()}
                          {' '}
                          <span className={new Date(dd.expiryDate) < new Date() ? 'text-destructive' : 'text-warning'}>
                            ({formatDistanceToNow(new Date(dd.expiryDate), { addSuffix: true })})
                          </span>
                        </p>
                      </div>
                    )}
                    {dd.fileUrl && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs text-muted-foreground">File URL</Label>
                        <a href={dd.fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">
                          {dd.fileUrl}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {isPhysical && (
                  <>
                    {pd?.status === 'AVAILABLE' && (
                      <Button
                        className="w-full justify-start gap-2"
                        onClick={openAssign}
                        disabled={mutations.assign.isPending}
                      >
                        <UserCheck className="h-4 w-4" />
                        {mutations.assign.isPending ? 'Assigning...' : 'Assign Asset'}
                      </Button>
                    )}
                    {pd?.status === 'ASSIGNED' && (
                      <>
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={openReturn}
                          disabled={mutations.returnAsset.isPending}
                        >
                          <RotateCcw className="h-4 w-4" />
                          {mutations.returnAsset.isPending ? 'Returning...' : 'Return Asset'}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={openTransfer}
                          disabled={mutations.transfer.isPending}
                        >
                          <RotateCcw className="h-4 w-4" />
                          {mutations.transfer.isPending ? 'Transferring...' : 'Transfer Asset'}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => window.location.href = `/app/settings/assets/${asset.id}`}
                    >
                      <Edit className="h-4 w-4" />
                      Edit Asset
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => window.location.href = `/app/settings/assets?assetType=${asset.assetType}`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Back to List
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full justify-start gap-2"
                      onClick={handleDelete}
                      disabled={deletingId === asset.id}
                    >
                      <Trash2 className="h-4 w-4" />
                      {deletingId === asset.id ? 'Deleting...' : 'Delete Asset'}
                    </Button>
                  </>
                )}
                {!isPhysical && dd?.fileUrl && (
                  <Button variant="outline" className="w-full justify-start gap-2" onClick={handlePreview}>
                    <Eye className="h-4 w-4" />
                    Preview File
                  </Button>
                )}
                {!isPhysical && dd?.fileUrl && (
                  <Button variant="outline" className="w-full justify-start gap-2" onClick={() => window.open(dd.fileUrl, '_blank')}>
                    <Download className="h-4 w-4" />
                    Download File
                  </Button>
                )}
                {!isPhysical && (
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => window.location.href = `/app/settings/assets?assetType=${asset.assetType}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back to List
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Purchase Tab */}
        {isPhysical && (
          <TabsContent value="purchase" className="pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Purchase Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {pd?.purchaseDate && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Purchase Date</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(pd.purchaseDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {pd?.purchasePrice && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Purchase Price</Label>
                      <p className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        ₹{pd.purchasePrice.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {pd?.currentValue && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Current Value</Label>
                      <p className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        ₹{pd.currentValue.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {pd?.vendor && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Vendor</Label>
                      <p>{pd.vendor}</p>
                    </div>
                  )}
                  {pd?.invoiceNumber && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Invoice Number</Label>
                      <p className="font-mono">{pd.invoiceNumber}</p>
                    </div>
                  )}
                  {pd?.purchaseOrderNumber && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">PO Number</Label>
                      <p className="font-mono">{pd.purchaseOrderNumber}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Assignment Tab */}
        {isPhysical && (
          <TabsContent value="assignment" className="pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Assignment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  {pd?.assignedToName && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Assigned To</Label>
                      <p className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.assignedToName}
                      </p>
                    </div>
                  )}
                  {pd?.department && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Department</Label>
                      <p className="flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.department}
                      </p>
                    </div>
                  )}
                  {pd?.location && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Location</Label>
                      <p className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        {pd.location}
                      </p>
                    </div>
                  )}
                  {pd?.assignmentDate && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Assignment Date</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(pd.assignmentDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Current Status</Label>
                    <StatusBadge status={pd?.status || asset.status} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Warranty Tab */}
        {isPhysical && (
          <TabsContent value="warranty" className="pt-4">
            {(() => {
              const status = getWarrantyStatus()
              if (!status) return (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                      <div className="p-2 rounded-full bg-white border">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">Warranty Status</p>
                        <p className="text-xs text-muted-foreground">No warranty period set</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
              return (
                <>
                  <Card className="mb-4">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                        <div className="p-2 rounded-full bg-white border">
                          <Shield className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">Warranty Status</p>
                          <p className="text-xs text-muted-foreground">
                            {asset.physicalData?.warrantyStartDate && asset.physicalData?.warrantyEndDate
                              ? `${new Date(asset.physicalData.warrantyStartDate).toLocaleDateString()} — ${new Date(asset.physicalData.warrantyEndDate).toLocaleDateString()}`
                              : 'No warranty period set'}
                          </p>
                        </div>
                        <Badge variant="outline" className={`${status.color} text-xs font-medium px-3 py-1.5`}>
                          <status.icon className="h-3 w-3 mr-1.5" />
                          {status.label}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Warranty Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        {pd?.warrantyStartDate && (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Warranty Start Date</Label>
                            <p className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              {new Date(pd.warrantyStartDate).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {pd?.warrantyEndDate && (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Warranty End Date</Label>
                            <p className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              {new Date(pd.warrantyEndDate).toLocaleDateString()}
                              {' '}
                              <span className={
                                new Date(pd.warrantyEndDate) < new Date() ? 'text-destructive' :
                                new Date(pd.warrantyEndDate).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000 ? 'text-warning' : 'text-emerald-600'
                              }>
                                ({new Date(pd.warrantyEndDate) < new Date() ? 'Expired' :
                                new Date(pd.warrantyEndDate).getTime() - Date.now() < 30 * 24 * 60 * 60 * 1000 ? 'Expiring Soon' : 'Active'})
                              </span>
                            </p>
                          </div>
                        )}
                        {pd?.warrantyProvider && (
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Warranty Provider</Label>
                            <p>{pd.warrantyProvider}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )
            })()}
          </TabsContent>
        )}

        {/* Maintenance Tab */}
        {isPhysical && (
          <TabsContent value="maintenance" className="pt-4">
            {/* Send for Maintenance Button */}
            <div className="flex justify-end mb-4">
              <Button onClick={openSendMaintenance} disabled={pd?.status === 'UNDER_MAINTENANCE'}>
                <Wrench className="mr-2 h-4 w-4" />
                Send for Maintenance
              </Button>
            </div>

            {/* Maintenance History */}
            <Card>
              <CardHeader>
                <CardTitle>Maintenance History</CardTitle>
              </CardHeader>
              <CardContent>
                {maintenanceQuery.isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4 animate-pulse">
                        <div className="h-10 w-10 rounded-full bg-muted" />
                        <div className="flex-1 space-y-1">
                          <div className="h-4 bg-muted rounded w-1/4" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : maintenanceRecords.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                    <Wrench className="h-12 w-12 mb-4 text-slate-300" />
                    <p className="text-sm font-medium">No maintenance records yet</p>
                    <p className="text-xs mt-1">Maintenance records will appear here when asset is sent for maintenance</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {maintenanceRecords.map((record: any, index: number) => (
                      <div
                        key={record.id || record._id || index}
                        className={`flex items-start gap-4 p-4 border-b last:border-0 transition-colors hover:bg-muted/30 ${
                          index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                        }`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted border">
                          <Wrench className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {record.maintenanceType === 'PREVENTIVE' ? 'Preventive' : record.maintenanceType === 'CORRECTIVE' ? 'Corrective' : record.maintenanceType === 'UPGRADE' ? 'Upgrade' : record.maintenanceType === 'CALIBRATION' ? 'Calibration' : record.maintenanceType === 'EMERGENCY' ? 'Emergency' : record.maintenanceType}
                            </Badge>
                            <span className="font-medium text-sm">{record.description}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(record.maintenanceDate), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Vendor: {record.vendor || '—'}
                            {record.cost && ` • Cost: ₹${Number(record.cost).toLocaleString()}`}
                          </p>
                          {record.nextMaintenanceDate && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Next Due: {new Date(record.nextMaintenanceDate).toLocaleDateString()}
                              {new Date(record.nextMaintenanceDate) < new Date() && record.status !== 'COMPLETED' && (
                                <span className="ml-2 text-destructive">(Overdue)</span>
                              )}
                            </p>
                          )}
                          {record.notes && (
                            <p className="text-sm text-foreground mt-1">{record.notes}</p>
                          )}
                          <div className="mt-2 flex items-center gap-2">
                            <Badge
                              variant={
                                record.status === 'COMPLETED' ? 'default' :
                                record.status === 'IN_PROGRESS' ? 'secondary' : 'outline'
                              }
                              className="text-xs"
                            >
                              {record.status === 'COMPLETED' ? 'Completed' : record.status === 'IN_PROGRESS' ? 'In Progress' : 'Scheduled'}
                            </Badge>
                            {(record.status === 'SCHEDULED' || record.status === 'IN_PROGRESS') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCompleteMaintenance(record.id || record._id)}
                                disabled={maintenanceMutations.complete.isPending}
                              >
                                <ShieldCheck className="mr-1.5 h-3 w-3" />
                                Mark Complete
                              </Button>
                            )}
                            {(record.status === 'SCHEDULED' || record.status === 'IN_PROGRESS') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelMaintenance(record.id || record._id)}
                                disabled={maintenanceMutations.cancel.isPending}
                                className="text-destructive hover:text-destructive"
                              >
                                <X className="mr-1.5 h-3 w-3" />
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          {new Date(record.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Documents Tab */}
        {isPhysical && (
          <TabsContent value="documents" className="pt-4">
            <div className="flex justify-between items-center mb-4">
              <CardTitle className="text-lg">Documents</CardTitle>
              <Button onClick={openUploadDocument}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </div>
            <Card>
              <CardContent>
                {documentsQuery.isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4 animate-pulse">
                        <div className="h-10 w-10 rounded-full bg-muted" />
                        <div className="flex-1 space-y-1">
                          <div className="h-4 bg-muted rounded w-1/4" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : documents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                    <FolderOpen className="h-12 w-12 mb-4 text-slate-300" />
                    <p className="text-sm font-medium">No documents yet</p>
                    <p className="text-xs mt-1">Upload documents like purchase invoices, warranty cards, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {documents.map((doc: any, index: number) => (
                      <div
                        key={doc.id || doc._id || index}
                        className={`flex items-center gap-4 p-4 border-b last:border-0 transition-colors hover:bg-muted/30 ${
                          index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                        }`}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted border">
                          <File className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {DOCUMENT_TYPE_LABELS[doc.documentType as AssetDocumentType] || doc.documentType}
                            </Badge>
                            <span className="font-medium text-sm truncate">{doc.fileName}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(doc.uploadedAt), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Type: {doc.fileType} • Size: {doc.fileSize < 1024 ? `${doc.fileSize} B` : doc.fileSize < 1024 * 1024 ? `${(doc.fileSize / 1024).toFixed(1)} KB` : `${(doc.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                            {doc.uploadedByName && ` • Uploaded by: ${doc.uploadedByName}`}
                          </p>
                          {doc.description && (
                            <p className="text-sm text-foreground mt-1">{doc.description}</p>
                          )}
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(doc.fileUrl, '_blank')}
                            >
                              <ExternalLink className="mr-1.5 h-3 w-3" />
                              View
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.id || doc._id)}
                              disabled={documentMutations.remove.isPending}
                            >
                              <Trash className="mr-1.5 h-3 w-3" />
                              Remove
                            </Button>
                          </div>
                        </div>
                        <div className="text-right text-xs text-muted-foreground whitespace-nowrap shrink-0">
                          {new Date(doc.uploadedAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* History Tab */}
        <TabsContent value="history" className="pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Asset History / Audit Trail</CardTitle>
            </CardHeader>
            <CardContent>
              {historyQuery.isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 animate-pulse">
                      <div className="h-10 w-10 rounded-full bg-muted" />
                      <div className="flex-1 space-y-1">
                        <div className="h-4 bg-muted rounded w-1/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                  <History className="h-12 w-12 mb-4 text-slate-300" />
                  <p className="text-sm font-medium">No history records yet</p>
                  <p className="text-xs mt-1">History will appear here as actions are performed on this asset</p>
                </div>
              ) : (
                <div className="space-y-0">
                  {history.map((record: any, index: number) => (
                    <div
                      key={record.id || record._id || index}
                      className={`flex items-start gap-4 p-4 border-b last:border-0 transition-colors hover:bg-muted/30 ${
                        index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                      }`}
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted border">
                        <ActionIcon action={record.action} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{ActionLabel({ action: record.action })}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(record.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          By {record.performedByName || 'Unknown User'}
                          {record.fieldName && ` • Field: ${record.fieldName}`}
                        </p>
                        {record.notes && (
                          <p className="text-sm text-foreground mt-1">{record.notes}</p>
                        )}
                        {(record.previousValue || record.newValue) && (
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            {record.previousValue && (
                              <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                                <X className="h-3 w-3" />
                                Previous: {record.previousValue}
                              </span>
                            )}
                            {record.newValue && (
                              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                <ChevronRight className="h-3 w-3" />
                                New: {record.newValue}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-muted-foreground whitespace-nowrap shrink-0">
                        {new Date(record.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
            <p className="text-sm text-muted-foreground">Assign "{asset?.name}" to an employee</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="assign-employee">Employee *</Label>
              <select
                id="assign-employee"
                value={assignForm.assignedTo}
                onChange={(e) => {
                  const user = users.find((u: { id: string; department?: string }) => u.id === e.target.value)
                  setAssignForm(prev => ({
                    ...prev,
                    assignedTo: e.target.value,
                    department: user?.department || prev.department,
                  }))
                }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Select employee</option>
                {users.map((user: { id: string; name: string; email: string; department?: string }) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email}){user.department ? ` - ${user.department}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-department">Department</Label>
              <input
                id="assign-department"
                value={assignForm.department}
                onChange={(e) => setAssignForm(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g. Engineering, Sales, Marketing"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-location">Location</Label>
              <input
                id="assign-location"
                value={assignForm.location}
                onChange={(e) => setAssignForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Building A, Floor 2, Desk 15"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-date">Assignment Date *</Label>
              <input
                id="assign-date"
                type="date"
                value={assignForm.assignmentDate}
                onChange={(e) => setAssignForm(prev => ({ ...prev, assignmentDate: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-notes">Notes</Label>
              <textarea
                id="assign-notes"
                value={assignForm.notes}
                onChange={(e) => setAssignForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this assignment"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={mutations.assign.isPending}>
              {mutations.assign.isPending ? 'Assigning...' : 'Assign Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Return Asset</DialogTitle>
            <p className="text-sm text-muted-foreground">Return "{asset?.name}" from {asset?.physicalData?.assignedToName || 'employee'}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="return-notes">Notes</Label>
              <textarea
                id="return-notes"
                value={returnForm.notes}
                onChange={(e) => setReturnForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this return"
                rows={3}
                className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReturn} disabled={mutations.returnAsset.isPending}>
              {mutations.returnAsset.isPending ? 'Returning...' : 'Return Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Asset</DialogTitle>
            <p className="text-sm text-muted-foreground">Transfer "{asset?.name}" to another employee</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="transfer-employee">New Employee *</Label>
              <select
                id="transfer-employee"
                value={transferForm.assignedTo}
                onChange={(e) => {
                  const user = users.find((u: { id: string; department?: string }) => u.id === e.target.value)
                  setTransferForm(prev => ({
                    ...prev,
                    assignedTo: e.target.value,
                    department: user?.department || prev.department,
                  }))
                }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Select employee</option>
                {users.map((user: { id: string; name: string; email: string; department?: string }) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email}){user.department ? ` - ${user.department}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-department">Department</Label>
              <input
                id="transfer-department"
                value={transferForm.department}
                onChange={(e) => setTransferForm(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g. Engineering, Sales, Marketing"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-location">Location</Label>
              <input
                id="transfer-location"
                value={transferForm.location}
                onChange={(e) => setTransferForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. Building A, Floor 2, Desk 15"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-date">Transfer Date *</Label>
              <input
                id="transfer-date"
                type="date"
                value={transferForm.transferDate}
                onChange={(e) => setTransferForm(prev => ({ ...prev, transferDate: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="transfer-notes">Notes</Label>
              <textarea
                id="transfer-notes"
                value={transferForm.notes}
                onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this transfer"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={mutations.transfer.isPending}>
              {mutations.transfer.isPending ? 'Transferring...' : 'Transfer Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send for Maintenance Dialog */}
      <Dialog open={sendMaintenanceDialogOpen} onOpenChange={setSendMaintenanceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Asset for Maintenance</DialogTitle>
            <p className="text-sm text-muted-foreground">Create a maintenance record for "{asset?.name}"</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-type">Maintenance Type *</Label>
              <select
                id="maintenance-type"
                value={sendMaintenanceForm.maintenanceType}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, maintenanceType: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="PREVENTIVE">Preventive</option>
                <option value="CORRECTIVE">Corrective</option>
                <option value="EMERGENCY">Emergency</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-description">Description *</Label>
              <textarea
                id="maintenance-description"
                value={sendMaintenanceForm.description}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the maintenance work needed"
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-vendor">Vendor / Service Provider</Label>
              <input
                id="maintenance-vendor"
                value={sendMaintenanceForm.vendor}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, vendor: e.target.value }))}
                placeholder="e.g. Dell Service Center, Local IT Vendor"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-cost">Estimated Cost (₹)</Label>
              <input
                id="maintenance-cost"
                type="number"
                value={sendMaintenanceForm.cost}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, cost: e.target.value }))}
                placeholder="0"
                step="0.01"
                min="0"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-date">Maintenance Date *</Label>
              <input
                id="maintenance-date"
                type="date"
                value={sendMaintenanceForm.maintenanceDate}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, maintenanceDate: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="next-maintenance-date">Next Maintenance Date</Label>
              <input
                id="next-maintenance-date"
                type="date"
                value={sendMaintenanceForm.nextMaintenanceDate}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, nextMaintenanceDate: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maintenance-notes">Notes</Label>
              <textarea
                id="maintenance-notes"
                value={sendMaintenanceForm.notes}
                onChange={(e) => setSendMaintenanceForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSendMaintenanceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendMaintenance} disabled={maintenanceMutations.create.isPending}>
              {maintenanceMutations.create.isPending ? 'Sending...' : 'Send for Maintenance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDocumentDialogOpen} onOpenChange={setUploadDocumentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <p className="text-sm text-muted-foreground">Upload a document for "{asset?.name}"</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="document-type">Document Type *</Label>
              <select
                id="document-type"
                value={uploadDocumentForm.documentType}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, documentType: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="PURCHASE_INVOICE">Purchase Invoice</option>
                <option value="WARRANTY_CARD">Warranty Card</option>
                <option value="AMC">AMC</option>
                <option value="REPAIR_INVOICE">Repair Invoice</option>
                <option value="DELIVERY_CHALLAN">Delivery Challan</option>
                <option value="ASSET_PHOTOGRAPH">Asset Photograph</option>
                <option value="OTHER">Other Document</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document-fileName">File Name *</Label>
              <input
                id="document-fileName"
                value={uploadDocumentForm.fileName}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, fileName: e.target.value }))}
                placeholder="e.g. invoice_12345.pdf"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document-fileUrl">File URL *</Label>
              <input
                id="document-fileUrl"
                value={uploadDocumentForm.fileUrl}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, fileUrl: e.target.value }))}
                placeholder="https://example.com/document.pdf"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document-fileType">File Type *</Label>
              <input
                id="document-fileType"
                value={uploadDocumentForm.fileType}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, fileType: e.target.value }))}
                placeholder="e.g. application/pdf"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document-fileSize">File Size (bytes) *</Label>
              <input
                id="document-fileSize"
                type="number"
                value={uploadDocumentForm.fileSize}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, fileSize: e.target.value }))}
                placeholder="1024000"
                min="0"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document-description">Description</Label>
              <textarea
                id="document-description"
                value={uploadDocumentForm.description}
                onChange={(e) => setUploadDocumentForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description"
                rows={2}
                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring resize-none"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setUploadDocumentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={documentMutations.add.isPending}>
              {documentMutations.add.isPending ? 'Uploading...' : 'Upload Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        {asset && (
          <DialogContent className="max-w-4xl max-h-[90vh] p-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${STATUS_COLORS[asset.status]}`}>
                  <CategoryIcon iconName={catIcon} className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">{asset.name}</p>
                  <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[asset.category]}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open(asset.fileUrl!, '_blank')} className="gap-1.5" disabled={!asset.fileUrl}>
                  <Download className="h-3.5 w-3.5" /> Download
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setPreviewOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-4">
              {asset.fileType === 'IMAGE' && asset.fileUrl && (
                <img src={asset.fileUrl} alt={asset.name} className="max-w-full max-h-[60vh] rounded-lg shadow-lg" />
              )}
              {asset.fileType === 'PDF' && asset.fileUrl && (
                <iframe
                  src={asset.fileUrl}
                  className="w-full h-[60vh] rounded-lg border border-border"
                  title={asset.name}
                />
              )}
              {!['IMAGE', 'PDF'].includes(asset.fileType || '') && (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium text-foreground">Preview unavailable</p>
                  <p className="text-sm text-muted-foreground mt-1">{asset.fileType || 'This file type'} cannot be previewed in the browser</p>
                  <Button onClick={() => window.open(asset.fileUrl!, '_blank')} className="mt-4 gap-1.5" disabled={!asset.fileUrl}>
                    <Download className="h-4 w-4" /> Download to view
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  )
}